// **The cue-gated stage HUD** (`demo-presentation-shell` tasks 8.1-8.4/8.7). Reacts to
// `prototype/sketches/demo-presentation-shell/stage-hud-states.html` and `chapter-title.html`.
//
// Everything here is presentational and stateless with respect to *visibility* — `DemoApp.tsx`
// owns the only decision that matters (`deriveUiState(sequencer.cursor, cueList)`, from group 3)
// and simply mounts these pieces when their own token is in `uiState.visible`. That is what makes
// "a chapter jump rebuilds the HUD to that chapter's state, never a union of what was previously
// shown" (task 8.3) true by construction: there is no HUD-local state to leak between chapters,
// only React mounting and unmounting components as the cursor-derived set changes.
//
// **The counters read the world** (wired at group 12/13's integration; they shipped with an em-dash
// placeholder while the render/adapter lane was still a parallel lane and `DemoApp` had no snapshot
// to read). `DemoApp` subscribes to the worker's snapshot stream and hands each counter its figure;
// a counter with nothing to read still shows `—` rather than an invented `0`, which would read as a
// real reading of zero rather than "not wired". `—` is the same "no figure to show" convention
// `TypeSheet.tsx`'s `ABSENT` already uses.
//
// Figures are thousands-separated (`toLocaleString`) per the kit's "numbers are the drama" rule,
// and the counters update only when their **displayed integer** changes — never at snapshot rate,
// which would re-render this subtree fifteen times a second to show the same two numbers.

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import { FESTIVAL_ASSETS } from './festival-assets';
import { Z } from './z-layers';

const PILL_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  background: 'var(--hud-bg)',
  border: '1.5px solid var(--hud-line)',
  borderRadius: 'var(--r-pill)',
  boxShadow: 'var(--shadow-hud)',
  padding: '9px 16px 9px 12px',
  fontFamily: 'var(--font-body)',
};

const DOT_STYLE = (background: string): CSSProperties => ({
  width: 22,
  height: 22,
  borderRadius: '50%',
  background,
  border: '2px solid var(--ink-1)',
  flex: '0 0 auto',
});

const NUMBER_STYLE: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 26,
  color: 'var(--hud-text)',
  lineHeight: 1,
};

const LABEL_STYLE: CSSProperties = {
  fontSize: 12,
  color: 'var(--hud-text-dim)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--tracking-caps)',
  fontWeight: 700,
};

/** Nothing to read — see module header. */
const NO_VALUE = '—';

function Counter({ dot, label, value }: { dot: string; label: string; value: number | null }): ReactElement {
  return (
    <div style={PILL_STYLE}>
      <div style={DOT_STYLE(dot)} />
      <div>
        <div style={NUMBER_STYLE}>{value === null ? NO_VALUE : value.toLocaleString()}</div>
        <div style={LABEL_STYLE}>{label}</div>
      </div>
    </div>
  );
}

export interface DemoCountersProps {
  showNectar: boolean;
  showBees: boolean;
  /** The presenter colony's stored nectar and living bee count, or `null` before any snapshot has
   * arrived (a bare stage mount, or the moments before the first tick). */
  nectar?: number | null;
  bees?: number | null;
}

/** The right-aligned counter rail (`stage-hud-states.html` panels A/B): the second counter
 * arrives beside the first without moving it, so neither reveal is a layout flicker on the
 * projector. */
export function DemoCounters({
  showNectar,
  showBees,
  nectar = null,
  bees = null,
}: DemoCountersProps): ReactElement {
  return (
    <div
      style={{
        position: 'absolute',
        top: 18,
        right: 18,
        display: 'flex',
        gap: 10,
        zIndex: Z.hudControls,
      }}
    >
      {showNectar && (
        <div data-testid="hud-nectarCounter">
          <Counter dot="var(--honey-500)" label="Nectar" value={nectar} />
        </div>
      )}
      {showBees && (
        <div data-testid="hud-beeCounter">
          <Counter dot="var(--bee-scout)" label="Bees" value={bees} />
        </div>
      )}
    </div>
  );
}

/** The chapter's title text — this change's own presenter-content decision (mirrors
 * `chapters.ts`'s canned stage content), transcribed verbatim from `docs/PRESENTATION.md`'s three
 * chapter headings. */
export const CHAPTER_TITLES: Record<'chapter1Title' | 'chapter2Title' | 'chapter3Title', { kicker: string; text: string }> = {
  chapter1Title: { kicker: 'Chapter 1', text: 'Language to communicate intent' },
  chapter2Title: { kicker: 'Chapter 2', text: "Automation the good ol' fashioned way" },
  chapter3Title: { kicker: 'Chapter 3', text: 'Enter Vibe Coding' },
};

/**
 * How long the wide band holds before it starts to fade (playthrough finding, 2026-08-09).
 *
 * It used to be a 2 600 ms *settle* into a corner card (`chapter-title.html` states A → B) that
 * then stayed up for the rest of the chapter. The presenter wants the opposite: the title
 * announces the chapter being entered and then gets out of the way of the world it is announcing,
 * so there is no state B at all — the band holds, fades, and is gone. Still chrome only, never a
 * cue the sequencer waits on (DD11: failsafes are cues, not timers; this is the one purely
 * cosmetic exception the sketch itself asks for).
 */
export const BAND_HOLD_MS = 1600;

/** The fade itself — both the CSS opacity transition's duration and how long after the hold the
 * band tells its caller it is finished. One number, so the callback can never fire before the
 * pixels have actually gone. */
export const BAND_FADE_MS = 450;
export const BAND_ENTER_MS = 450;

export interface ChapterTitleOverlayProps {
  kicker: string;
  text: string;
  /** Called once the fade has finished, so the caller can unmount the band. The overlay does not
   * hide itself: **whether** a title is on screen is `DemoApp`'s decision (it is what stops a
   * backward navigation re-announcing a chapter), and a component that self-hid would be a second
   * place that decision lives. */
  onDone?: () => void;
}

/**
 * `chapter-title.html` state A: a wide band over the (still-playing) world. Callers key this
 * component on the title token they are displaying (`key={displayedTitle}` in `DemoApp`) so a new
 * chapter's title remounts it and restarts the arrival rather than inheriting a mid-fade one.
 */
export function ChapterTitleOverlay({ kicker, text, onDone }: ChapterTitleOverlayProps): ReactElement {
  const [entered, setEntered] = useState(false);
  const [fading, setFading] = useState(false);
  // Held in a ref so the timers are started **once**, by a mount effect: `DemoApp` passes an inline
  // arrow, so a dependency on `onDone` would restart the hold on every render of the show.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  useEffect(() => {
    const enter = window.setTimeout(() => setEntered(true), 0);
    const fade = window.setTimeout(() => setFading(true), BAND_ENTER_MS + BAND_HOLD_MS);
    // A timer rather than a `transitionend` listener: jsdom runs no CSS engine, so a
    // transition-driven callback would never fire under test — and on the projector a dropped
    // transition event would leave the band up for the whole chapter, which is the bug being fixed.
    const done = window.setTimeout(
      () => onDoneRef.current?.(),
      BAND_ENTER_MS + BAND_HOLD_MS + BAND_FADE_MS,
    );
    return () => {
      window.clearTimeout(enter);
      window.clearTimeout(fade);
      window.clearTimeout(done);
    };
  }, []);

  const bandStyle: CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    // **The bottom third, not mid-screen** (2026-08-11 request, "all gameplay headings"). Centred
    // at 50% the band sat squarely over whatever bee the audience had just been asked to look at —
    // chapter 1's opening shot centres the camera on the one staged bee (`DemoApp`'s `stageFocus`)
    // for exactly that reason, and a title arriving on top of it undid the point of the framing.
    // `bottom` rather than a second `top`/`transform` pair: it anchors the band to the bottom edge
    // regardless of its own (content-driven) height, landing it inside the lower third of a 1080p
    // projector with the padding below.
    bottom: '8%',
    zIndex: Z.hudBar,
    overflow: 'hidden',
    background: '#FFFFFF',
    padding: '26px 56px',
    fontFamily: "'Figtree', ui-sans-serif, system-ui, sans-serif",
    opacity: entered && !fading ? 1 : 0,
    transition: `opacity ${fading ? BAND_FADE_MS : BAND_ENTER_MS}ms ease-out`,
    // The band sits over a world the presenter is clicking in; it must never eat a click.
    pointerEvents: 'none',
  };

  return (
    <div data-testid="demo-chapter-title" data-entered={entered} data-fading={fading} style={bandStyle}>
      <div
        data-testid="festival-chapter-element"
        style={{
          position: 'absolute',
          width: 720,
          height: 162,
          right: -80,
          bottom: 0,
          overflow: 'hidden',
          opacity: 0.9,
          pointerEvents: 'none',
        }}
      >
        <img
          src={FESTIVAL_ASSETS.elementTwo}
          alt=""
          aria-hidden="true"
          style={{ position: 'absolute', width: 'auto', height: 240, left: 280, top: -77 }}
        />
      </div>
      <div
        style={{
          position: 'relative',
          fontSize: 13,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          color: '#BC120E',
        }}
      >
        {kicker}
      </div>
      <div
        style={{
          position: 'relative',
          fontFamily: 'inherit',
          fontWeight: 500,
          fontSize: 38,
          color: '#000000',
          lineHeight: 1.15,
          marginTop: 4,
        }}
      >
        {text}
      </div>
    </div>
  );
}

// The prompt bar's real chrome — submission, scope chip switching, busy/failure states — is
// `PromptBar.tsx` (task group 10, design **DD6**), rendered from `DemoApp.tsx` through
// `InstructPromptBar`/`BeekeeperPromptBar`. The placeholder this group shipped ahead of that
// (`PromptBarPlaceholder`) is retired now that nothing mounts it.
